/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { AnalyticsConstants, Diagnostics, Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import { ActionKernelType } from 'client-code/actions';
import { AiringOffer } from 'client-code/airing';

import { INFO_BANNER_HIDE_ANIMATION_TIMEOUT_VALUE } from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { Constants } from '../../core/constants';
import { DeviceFeatures, DeviceModel } from '../../core/device';
import { Channel } from '../../core/entities';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../core/interactors';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { QoEAppLogger } from '../../core/logging';
import { Log } from '../../core/logging/Log';
import { QoEConnectionModeType } from '../../core/logging/qoe/QoEConnectionModeType';
import { VuLogger } from '../../core/logging/VULogger';
import { CursorManager } from '../../core/navigation';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../core/network/NetworkManager';
import { PlaybackContentType } from '../../core/playback/enums';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import { Storage } from '../../core/storage/Storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TOfferPreviewCustomData } from '../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { RecordingRestriction } from '../../core/usecases';
import {
    ErrorPriority,
    MetadataState,
    MetadataType,
    PlaybackRestrictionType,
    VideoPlayerStates,
} from '../../core/videoplayer/enums';
import { IPlayable, IPlayerStatus } from '../../core/videoplayer/interfaces';
import { AiringPreviewUtils } from '../../core/videoplayer/utils';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { clearOnePassSharedPreference } from '../../store/overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import { overlayDisableGhostMode } from '../../store/overlay/overlayhighlight/overlayhighlight.reducers';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { clearScheduleRecordingSharedPreference } from '../../store/overlay/recordingoverlay/recordingoverlay.reducers';
import {
    clearBookmarkSharedPreference,
    clearDiskSpaceSharedPreference,
} from '../../store/overlay/recordoverlay/recordoverlay.reducers';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import { setScreenTaskController } from '../../store/screens/screens.reducers';
import { hideSpinner } from '../../store/spinner/spinner.reducers';
import { TOAST_MESSAGE_TYPE } from '../../store/toastmessage/toastmessage.constants';
import {
    onKeyBackDeleteRecordingOverlay,
    showDeleteRecordingOverlay,
} from '../../store/videoplayer/overlays/deleterecording/deleterecording.reducers';
import {
    videoPlayerSetPlaybackStateAction,
    videoPlayerToggleIsPlayingAction,
} from '../../store/videoplayer/videoplayer.actions';
import {
    disableStillThereTimer,
    handleTrickplaySpeedOnNetworkConnection,
    resetStillThereTimeout,
    toggleIsPlaying,
} from '../../store/videoplayer/videoplayer.reducers';
import {
    hideInfoBanners,
    hideTrickPlayMenu,
    showStandardInfo,
    showTrickPlayMenuOnStartUp,
    updateTrickPlayMenuTimer,
} from '../../store/videoplayerui/infobanner/infobanner.reducers';
import { PlaybackState } from '../../store/videoplayerui/infobanner/PlaybackTypes';
import { hideOneLineGuide } from '../../store/videoplayerui/onelineguide/onelineguide.reducers';
import { hideErrorOSD, showErrorOSD } from '../../store/videoplayerui/osd/osd.reducers';
import {
    clearDebounceTimerVideoPlayerUi,
    swtichToPreviousControllerFromInteractiveLayer,
} from '../../store/videoplayerui/videoplayeruinavigation.reducers';
import { ToastMessage } from '../common';
import { Whisper } from '../Whisper';
import { ActionsStrip } from './ActionsStrip';
import { ActionsStripProps } from './ActionsStrip/ActionsStripProps';
import { EventDescription } from './FullInfoBanner/EventDescription';
import { RootInteractiveLayer } from './InteractiveLayer';
import { OneLineGuide } from './OneLineGuide';
import { Osd } from './OSD';
import { OsdTypes } from './OSD/OSDTypes';
import { Spinner } from './Spinner';
import { SpinnerPositionTypes } from './Spinner/SpinnerPositionTypes';
import { StandardInfoBanner } from './StandardInfoBanner';
import { TrickplayBanner } from './Trickplay';
import { VideoDebugOverlay } from './VideoDebugOverlay';

import './VideoPlayerUI.scss';

const VideoPlayerUI: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'VideoPlayerUI';

    const dispatch = useTivoDispatch();
    const isTizenPlatform = DeviceModel.isTizenPlatform();
    const oIVideoPlayerController: VideoPlaybackController = VideoPlaybackController.getInstance();
    const services = new ServicesClientImpl();

    const actionsStripOptionDefaultValue = {
        name: '',
        visible: false,
        message: '',
    };

    // useTivoSelectors
    const { isErrorOSDActive, errorCode, errorMessage, errorPriority } = useTivoSelector(
        (state: RootState) => state.errorOsd,
    );
    const { isOverlayDialogActive, uiProperties } = useTivoSelector(
        (state: RootState) => state.overlayDialog,
    );

    const { playbackState } = useTivoSelector((state: RootState) => state.videoPlayer);
    const { isSpinnerActive, spinnerMessage, errorCodes } = useTivoSelector(
        (state: RootState) => state.spinner,
    );
    const { isStillThereOsdActive, isStillThere } = useTivoSelector(
        (state: RootState) => state.videoPlayer,
    );
    const { showOneLineGuide: isOneLineGuideActive } = useTivoSelector(
        (state: RootState) => state.oneLineGuide,
    );
    const { isVideoDebugOverlayActive } = useTivoSelector(
        (state: RootState) => state.videoDebugOverlay,
    );
    const { isActionsStripActive, isFullInfoActive, isStandardInfoActive, isTrickplayMenuActive } =
        useTivoSelector((state: RootState) => state.infoBanner);

    // useStates
    const [mPlayable, setmPlayable] = useState<IPlayable | null>(
        oIVideoPlayerController.getPlayable(),
    );
    const [actionsStripAnimationWait, setActionsStripAnimationWait] = useState(false);
    const [airingOffers, setAiringOffers] = useState<AiringOffer[]>(
        oIVideoPlayerController.getMetadata().airingOffers || [],
    );
    const [bookMarkOption, setBookMarkOption] = useState(actionsStripOptionDefaultValue);
    const [channel, setChannel] = useState<Channel | undefined>(
        VideoPlaybackController.getInstance().getPlayable()?.channel,
    );
    const [createOnepassOption, setCreateOnepassOption] = useState(actionsStripOptionDefaultValue);
    const isSubscribed = useRef(
        oIVideoPlayerController.getPlayable()?.channel?.isSubscribed || false,
    );
    const channelNumber = useRef(
        oIVideoPlayerController.getPlayable()?.channel?.channelNumber || '',
    );
    const [recordOption, setRecordOption] = useState(actionsStripOptionDefaultValue);
    const [stopRecordingOption, setStopRecordingOption] = useState(actionsStripOptionDefaultValue);
    const [currentAudioTrackSelected, setCurrentAudioTrackSelected] = useState('');
    const [currentTextTrack, setCurrentTextTrack] = useState('');
    const [fullInfoAnimationWait, setFullInfoAnimationWait] = useState(false);
    const [hideBannerTipAtProgramBoundary, setHideBannerTipAtProgramBoundary] = useState(false);
    const [isCursorEnabled, setIsCursorEnabled] = useState(CursorManager.isCursorAppeared());
    const [isFullInfoDataFetched, setIsFullInfoDataFetched] = useState<boolean>(false);
    const [metadataState, setMetadataState] = useState(MetadataState.PREPARING);
    const [modifyOnepassOption, setModifyOnepassOption] = useState(actionsStripOptionDefaultValue);
    const [videoPlayerState, setVideoPlayerState] = useState(VideoPlayerStates.NONE);
    const [offerPreview, setOfferPreview] = useState<TOfferPreviewCustomData>(
        oIVideoPlayerController.getMetadata().offerPreview || {},
    );
    const [standardInfoAnimationWait, setStandardInfoAnimationWait] = useState(false);
    const [trickplayAnimationWait, setTrickplayAnimationWait] = useState(false);
    const [isAppInForeground, setIsAppInForeground] = useState(true);

    // useRefs
    const isScreenActive = React.useRef(false);
    const onceOnVideoLaunch = useRef(false);
    const prevNetworkState = useRef(true);
    const screenEntryTime = useRef(0);
    const previousOfferPreview = useRef(offerPreview);
    const previousChannel = useRef(channel);
    const previousAiringOffers = useRef(airingOffers);

    // updates the previous values with latest values only if offerPreview is updated
    if (previousOfferPreview.current !== offerPreview) {
        previousChannel.current = channel;
        previousOfferPreview.current = offerPreview;
        previousAiringOffers.current = airingOffers;
    }

    const dataFormatter = new AiringPreviewUtils(airingOffers, offerPreview);
    const infoBannerDivAnimationWait =
        !isTizenPlatform &&
        (standardInfoAnimationWait || actionsStripAnimationWait || fullInfoAnimationWait);
    let isAdult;
    if (airingOffers)
        isAdult = airingOffers[0]?.parentalControls?.isAdult && services.shouldHideMetadata();
    const isErrorOverlayActive =
        isOverlayDialogActive && uiProperties.overlayType === OverlayTypes.ERROR;
    const isNetworkConnected = oIVideoPlayerController.isNetworkConnected();
    const isInfobannerContainerActive =
        isActionsStripActive ||
        isFullInfoActive ||
        isStandardInfoActive ||
        isTrickplayMenuActive ||
        (isNetworkConnected && oIVideoPlayerController.isTrickplayEnabled());
    const isNetworkOsdActive: boolean =
        isErrorOSDActive && errorPriority === ErrorPriority.NO_NETWORK_ERROR;
    const isSubtitlesCCLanguageVisible: boolean =
        !!currentTextTrack && oIVideoPlayerController.isTextTrackChangeSupported();
    let osdType = OsdTypes.DEFAULT;

    const recordingRestrictionDetails = RecordingRestriction.getRestrictionDetails(
        RecordingRestriction.ACTION_STRIP,
        channel,
        undefined,
        offerPreview,
    );
    const recordingRestrictionMessages = recordingRestrictionDetails.message.split(
        RecordingRestriction.MESSAGE_SEPARATOR,
    );
    const isPlayingRecording = VideoPlaybackController.getInstance().isNdvr();
    const isRecordingRestrictionVisible =
        recordingRestrictionDetails.isRestricted &&
        !isPlayingRecording &&
        !(recordOption.visible || stopRecordingOption.visible);

    const [actionItems, setActionItems] = useState<ActionsStripProps['actionItems']>([]);
    const [hideActionItems, setHideActionItems] = useState<boolean>(false);
    /**
     * This useEffect is to reset the actionsStrip NDVR options to empty on channel change
     */
    useEffect(() => {
        setBookMarkOption(actionsStripOptionDefaultValue);
        setCreateOnepassOption(actionsStripOptionDefaultValue);
        setModifyOnepassOption(actionsStripOptionDefaultValue);
        setRecordOption(actionsStripOptionDefaultValue);
        setStopRecordingOption(actionsStripOptionDefaultValue);
        setHideActionItems(false);
    }, [channel, mPlayable]);

    useEffect(() => {
        const tempActionItems = [
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_RESUME),
                visible: videoPlayerState === VideoPlayerStates.PAUSE && !isNetworkOsdActive,
            },
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_CHANGE_AUDIO_TRACK),
                message: currentAudioTrackSelected
                    ? `${Locale.getString(
                          LocaleKeys.LIVE_TV_CHANGE_AUDIO_TRACK_MESSAGE,
                      )}  ${currentAudioTrackSelected}`
                    : '',
                visible: true,
            },
            {
                name: services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER),
                visible:
                    DeviceFeatures.isSocuEnabled() &&
                    dataFormatter.getIsAvailableInSocu() &&
                    isSubscribed.current &&
                    hideActionItems,
            },
            {
                name: recordingRestrictionMessages[0] ?? '',
                message: recordingRestrictionMessages[1] ?? '',
                visible: isRecordingRestrictionVisible && hideActionItems,
                prependIconClass: RecordingRestriction.ACTION_STRIP,
            },
            recordOption,
            stopRecordingOption,
            createOnepassOption,
            modifyOnepassOption,
            bookMarkOption,
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_SEE_MORE_INFO),
                visible: hideActionItems,
                message: '',
            },
            {
                name: `${
                    oIVideoPlayerController.isClosedCaptionActive()
                        ? Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_TURN_SUBTITLES_CC_OFF)
                        : Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_TURN_SUBTITLES_CC_ON)
                }`,
                message: `${Locale.getString(LocaleKeys.LIVE_TV_TURN_SUBTITLES_CC_ON_MESSAGE)}
                         ${
                             oIVideoPlayerController.isClosedCaptionActive()
                                 ? Locale.getString(LocaleKeys.LIVE_TV_ON)
                                 : Locale.getString(LocaleKeys.LIVE_TV_OFF)
                         }`,
                visible: oIVideoPlayerController.isTextTrackSupported() && hideActionItems,
            },
            {
                name: Locale.getString(
                    LocaleKeys.LIVE_TV_ACTION_STRIP_CHANGE_SUBTITLES_CC_LANGUAGE,
                ),
                message: `${Locale.getString(
                    LocaleKeys.LIVE_TV_CHANGE_SUBTITLES_CC_LANGUAGE_MESSAGE,
                )}  ${currentTextTrack}`,
                visible:
                    isSubtitlesCCLanguageVisible &&
                    oIVideoPlayerController.isClosedCaptionActive() &&
                    hideActionItems,
            },
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_STREAM_STATS),
                message: Locale.getString(LocaleKeys.LIVE_TV_STREAM_STATS),
                visible:
                    Storage.getInstance().getItem(Storage.Keys.IS_VIDEO_DEBUG_OVERLAY_ACTIVE) &&
                    hideActionItems,
            },
        ];

        setActionItems(tempActionItems);
    }, [
        channel,
        recordOption,
        stopRecordingOption,
        createOnepassOption,
        currentTextTrack,
        modifyOnepassOption,
        bookMarkOption,
        currentAudioTrackSelected,
        videoPlayerState,
        isNetworkOsdActive,
        dataFormatter.getIsAvailableInSocu(),
        oIVideoPlayerController.isTextTrackSupported(),
        isSubtitlesCCLanguageVisible,
        oIVideoPlayerController.isClosedCaptionActive(),
    ]);

    useEffect(() => {
        if (actionItems.length > 2) {
            setHideActionItems(true);
        }
    }, [actionItems]);

    const handlePlaybackRestriction = (type: PlaybackRestrictionType) => {
        switch (type) {
            case PlaybackRestrictionType.APP_BACKGROUND:
                setIsAppInForeground(false);
                break;
            case PlaybackRestrictionType.APP_FOREGROUND:
                setIsAppInForeground(true);
                break;
            case PlaybackRestrictionType.NO_RESTRICTION:
            case PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE:
                dispatch(hideErrorOSD());
                break;
            case PlaybackRestrictionType.NETWORK_CONNECTED:
                onNetworkConnected();
                break;
            case PlaybackRestrictionType.NETWORK_DISCONNECTED:
                onNetworkDisconnected();
                break;
            default:
        }
    };

    const handleMetadataStateChange = (state: MetadataState) => {
        setMetadataState(state);
        switch (state) {
            case MetadataState.AIRING_ERROR:
                onAiringOffersError();
                setIsFullInfoDataFetched(false);
                break;
            case MetadataState.AIRING_READY:
                onAiringOffersReady();
                break;
            case MetadataState.DELETE_RECORDING_DONE:
            case MetadataState.DELETE_RECORDING_ERROR:
                // TODO: KST-7544 After implementation of screen stack, go to Guide/MyShows
                dispatch(onKeyBackDeleteRecordingOverlay());
                break;
            case MetadataState.PREVIEW_ERROR:
                setOfferPreview({});
                break;
            case MetadataState.PREVIEW_READY:
                setOfferPreview(oIVideoPlayerController.getMetadata().offerPreview || {});
                break;
            case MetadataState.RECORDING_PREVIEW_ERROR:
                onRecordingPreviewError();
                break;
            case MetadataState.RECORDING_PREVIEW_READY:
                onRecordingPreviewReady();
                break;
            case MetadataState.RECORDING_ACTION_READY:
            case MetadataState.ACTION_LIST_READY:
            case MetadataState.VOD_ACTION_READY:
                onActionListReady();
                break;
            case MetadataState.VOD_PREVIEW_READY:
                setAiringOffers([]);
                setOfferPreview(oIVideoPlayerController.getMetadata().offerPreview || {});
                break;
            case MetadataState.VOD_PREVIEW_ERROR:
                setOfferPreview({});
                break;
            default:
        }
    };

    const handleVideoStateChange = (status: IPlayerStatus): void => {
        setVideoPlayerState(status.state);
        if (status.state === VideoPlayerStates.END_OF_STREAM) {
            if (oIVideoPlayerController.isNdvr()) {
                dispatch(showDeleteRecordingOverlay());
            }
        }
    };

    const onActionListReady = () => {
        clearBookmarkSharedPreference();
        clearDiskSpaceSharedPreference();
        clearOnePassSharedPreference();
        clearScheduleRecordingSharedPreference();
        const actionListData = VideoPlaybackController.getInstance().getMetadata().actionList;
        const airingPreviewUtils = new AiringPreviewUtils(
            [],
            VideoPlaybackController.getInstance().getMetadata().offerPreview,
        );
        const channelTitle = airingPreviewUtils.getChannelTitle();
        const episodeTitle = airingPreviewUtils.getEpisodeTitle();
        const seasonEpisode = airingPreviewUtils.getSeasonEpisode();
        const actionStripRecordOptionMessage = `${channelTitle} ${seasonEpisode ? `| ${seasonEpisode}` : ''} ${episodeTitle}`;
        const onePassSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
        ).edit();
        if (!AppUtils.isEmpty(actionListData?.actions)) {
            actionListData.actions.forEach((action) => {
                const {
                    actionKernelType,
                    actionId,
                    contentId,
                    offerId,
                    channelNumber: ndvrChannelNumber,
                } = action.kernel;
                if (ndvrChannelNumber && ndvrChannelNumber !== channelNumber.current) {
                    services
                        .getChannelFromChannelNumber(ndvrChannelNumber)
                        .then((ndvrchannel: Channel) => {
                            setChannel(ndvrchannel);
                            channelNumber.current = ndvrchannel.channelNumber ?? '';
                            previousChannel.current = ndvrchannel;
                            isSubscribed.current = ndvrchannel.isSubscribed ?? false;
                            channelNumber.current = ndvrchannel.channelNumber ?? '';
                        });
                }
                const recordingSharedPreferenceEditor = new SharedPreferences(
                    SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                ).edit();

                if (offerId) {
                    recordingSharedPreferenceEditor.putString(
                        SharedPrefConstants.RECORDING_OFFER_ID,
                        offerId,
                    );
                }
                if (contentId)
                    recordingSharedPreferenceEditor.putString(
                        SharedPrefConstants.RECORDING_CONTENT_ID,
                        contentId,
                    );
                switch (actionKernelType) {
                    case ActionKernelType.BookmarkThisEventActionKernel:
                    case ActionKernelType.BookmarkThisEpisodeActionKernel:
                    case ActionKernelType.BookmarkThisGameActionKernel:
                    case ActionKernelType.BookmarkThisMatchActionKernel:
                    case ActionKernelType.BookmarkThisMovieActionKernel:
                    case ActionKernelType.BookmarkThisRaceActionKernel:
                    case ActionKernelType.BookmarkThisShowActionKernel:
                        {
                            const { collectionId } = action.kernel;
                            const bookmarkType = actionId.split('_');
                            const bookmarkKernelType = bookmarkType[bookmarkType.length - 1];

                            const bookmarkSharedPreferenceEditor = new SharedPreferences(
                                SharedPrefConstants.BOOKMARK_ACTION_PREF_KEY,
                            ).edit();

                            bookmarkSharedPreferenceEditor.putString(
                                SharedPrefConstants.BOOKMARK_CONTENT_ID,
                                contentId,
                            );
                            bookmarkSharedPreferenceEditor.putString(
                                SharedPrefConstants.BOOKMARK_COLLECTION_ID,
                                collectionId,
                            );
                            bookmarkSharedPreferenceEditor.putString(
                                SharedPrefConstants.BOOKMARK_ACTION_KERNEL_TYPE,
                                bookmarkKernelType,
                            );
                            bookmarkSharedPreferenceEditor.commit();
                            setBookMarkOption({
                                name: `${Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_BOOKMARK)} ${Locale.getString(LocaleKeys[bookmarkKernelType])}`,
                                visible: true,
                                message: '',
                            });
                        }
                        break;

                    case ActionKernelType.CreateMultiOnePassActionKernel:
                    case ActionKernelType.CreateOnePassActionKernelV2:
                        onePassSharedPreferenceEditor.putJson(
                            SharedPrefConstants.CREATE_ONE_PASS_ACTION,
                            action,
                        );
                        onePassSharedPreferenceEditor.apply();
                        setCreateOnepassOption({
                            name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_CREATE_ONEPASS),
                            visible: true,
                            message: channelTitle,
                        });
                        setModifyOnepassOption(actionsStripOptionDefaultValue);
                        break;

                    case ActionKernelType.DeleteBookmarkActionKernel:
                        setBookMarkOption(actionsStripOptionDefaultValue);
                        break;

                    case ActionKernelType.ModifyMultiOnePassActionKernel:
                    case ActionKernelType.ModifyOnePassActionKernelV2:
                        onePassSharedPreferenceEditor.putJson(
                            SharedPrefConstants.MODIFY_ONE_PASS_ACTION,
                            action,
                        );
                        onePassSharedPreferenceEditor.apply();
                        setModifyOnepassOption({
                            name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_MODIFY_ONEPASS),
                            visible: true,
                            message: channelTitle,
                        });
                        setCreateOnepassOption(actionsStripOptionDefaultValue);
                        break;

                    case ActionKernelType.RecordThisEpisodeActionKernel:
                    case ActionKernelType.RecordThisEventActionKernel:
                    case ActionKernelType.RecordThisGameActionKernel:
                    case ActionKernelType.RecordThisMatchActionKernel:
                    case ActionKernelType.RecordThisRaceActionKernel:
                    case ActionKernelType.RecordThisMovieActionKernel:
                    case ActionKernelType.RecordThisShowActionKernel:
                    case ActionKernelType.RecordAgainActionKernel:
                        {
                            setStopRecordingOption(actionsStripOptionDefaultValue);
                            const recordType = actionId.split('_');
                            const recordKernelType = recordType[recordType.length - 1];
                            recordingSharedPreferenceEditor.putString(
                                SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE,
                                recordKernelType,
                            );
                            recordingSharedPreferenceEditor.putString(
                                SharedPrefConstants.RECORDING_ACTION_KERNEL,
                                actionKernelType,
                            );

                            setRecordOption({
                                name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_RECORD),
                                visible: true,
                                message: actionStripRecordOptionMessage,
                            });
                        }
                        break;

                    case ActionKernelType.StopAndDeleteRecordingActionKernel:
                        setRecordOption(actionsStripOptionDefaultValue);
                        setStopRecordingOption({
                            name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_STOP_RECORDING),
                            visible: true,
                            message: actionStripRecordOptionMessage,
                        });
                        break;

                    default:
                }
                recordingSharedPreferenceEditor.commit();
            });
        }
    };

    const onAiringOffersReady = () => {
        if (isOverlayDialogActive) dispatch(hideOverlayDialog());
        setAiringOffers(oIVideoPlayerController.getMetadata().airingOffers || []);
        setIsFullInfoDataFetched(false);
    };

    const onAiringOffersError = () => {
        setAiringOffers([]);
        dispatch(hideSpinner());
        Diagnostics.error(TAG, 'Airing data fail scenario');
        Log.e(TAG, 'Airing data fail scenario');
    };

    const onCursorStateChangeCallback = (state) => {
        setIsCursorEnabled(state);
        if (!state) {
            dispatch(swtichToPreviousControllerFromInteractiveLayer());
            dispatch(overlayDisableGhostMode());
        }
    };

    const onNetworkConnected = () => {
        Diagnostics.info(TAG, 'Network is connected, network osd hidden');
        Log.i(TAG, 'Network is connected, network osd hidden');
        dispatch(hideErrorOSD(true));
        dispatch(resetStillThereTimeout(true));
        Diagnostics.info(TAG, 'videoPlayerState after network gets back = ', videoPlayerState);
        Log.i(TAG, 'videoPlayerState after network gets back = ', videoPlayerState);

        // If video player state is not LOADED, reset PlaybackState
        if (!oIVideoPlayerController.isPlayerLoaded()) {
            dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
            dispatch(videoPlayerToggleIsPlayingAction(true));
            return;
        }

        VuLogger.logStreamingRecovery(VuLogger.StreamingRecoveryType.NETWORK_ERROR);
        dispatch(showTrickPlayMenuOnStartUp());
        dispatch(handleTrickplaySpeedOnNetworkConnection());
    };

    const onNetworkDisconnected = () => {
        oIVideoPlayerController.getTrickplayControl().stopScrubbing();
        if (isTrickplayMenuActive) dispatch(hideTrickPlayMenu());
        prevNetworkState.current = false;
        setIsFullInfoDataFetched(false);
        Diagnostics.info(TAG, 'Network lost, network osd shown');
        Log.i(TAG, 'Network lost, network osd shown');
        QoEAppLogger.updateConfigurationForConnectionMode(QoEConnectionModeType.OFFLINE);
        QoEAppLogger.stopMediaHealthTimer();
        QoEAppLogger.logNetworkLost(
            oIVideoPlayerController.getPlayerStats(),
            oIVideoPlayerController.getPlayerCurrentTime(),
            oIVideoPlayerController.getSeekRange(),
        );
    };

    const handlePlayableChange = (playable: IPlayable) => {
        setmPlayable(playable);
        if (playable.channel) {
            setChannel(playable.channel);
            isSubscribed.current = playable.channel.isSubscribed ?? false;
            channelNumber.current = playable.channel.channelNumber ?? '';
        }
    };

    const handleOnProgramBoundary = () => {
        Log.d(TAG, 'handleOnProgramBoundary');
        setHideBannerTipAtProgramBoundary(true);
        if (!(isFullInfoActive || isStandardInfoActive || isTrickplayMenuActive))
            dispatch(showStandardInfo());
    };

    const onRecordingPreviewError = () => {
        setIsFullInfoDataFetched(false);
        setOfferPreview({});
    };

    const onRecordingPreviewReady = () => {
        setAiringOffers([]);
        setOfferPreview(oIVideoPlayerController.getMetadata().offerPreview || {});
    };

    const updateScreenTaskOnDataStateChange = (state: UIOrchestratorState) => {
        if (state === UIOrchestratorState.FETCHING_DATA) {
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.PREPARING_DATA));
        }
        if (state === UIOrchestratorState.DATA_LOADED) {
            // setting it as DEFAULT_VIDEO_PLAYER_TASKS so that when overlays hide the previous
            // screen task is default and navigation is not interrupted
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS));
        }
    };

    useEffect(() => {
        /**
         * It is used to update the currentAudioTrackSelected state so it updates everytime in
         * ActionsStrip whenever current selected audio track is changed on player Loaded.
         */
        if (videoPlayerState === VideoPlayerStates.LOADED) {
            setCurrentAudioTrackSelected(oIVideoPlayerController.getSelectedAudioTrack());
        }
    }, [videoPlayerState]);

    useEffect(() => {
        UIOrchestrator.stateChangeListener.subscribe(updateScreenTaskOnDataStateChange);
        const payload: Payload = new Payload();
        screenEntryTime.current = Date.now();
        payload.add(
            AnalyticsConstants.Fields.SCREEN_NAME,
            AnalyticsConstants.Screens.LIVE_TV_SCREEN,
        );
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        VU.screen(payload);

        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        VideoPlaybackController.getInstance()
            .getRestrictionListener()
            .subscribe(handlePlaybackRestriction);

        VideoPlaybackController.getInstance()
            .getMetadataStateChangeListener()
            .subscribe(handleMetadataStateChange);
        VideoPlaybackController.getInstance()
            .getPlayerStateChangeListener()
            .subscribe(handleVideoStateChange);
        VideoPlaybackController.getInstance()
            .getProgramBoundaryListener()
            .subscribe(handleOnProgramBoundary);
        VideoPlaybackController.getInstance()
            .getPlayableChangeListener()
            .subscribe(handlePlayableChange);

        return () => {
            payload.add(
                AnalyticsConstants.Fields.SCREEN_TRANSITION,
                AnalyticsConstants.TransitionState.EXIT,
            );
            payload.add(
                AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
                Date.now() - screenEntryTime.current,
            );
            VU.screen(payload);
            UIOrchestrator.stateChangeListener.cancel(updateScreenTaskOnDataStateChange);
            CursorManager.unsubscribe(refCursorManager);
            VideoPlaybackController.getInstance()
                .getRestrictionListener()
                .cancel(handlePlaybackRestriction);
            VideoPlaybackController.getInstance()
                .getMetadataStateChangeListener()
                .cancel(handleMetadataStateChange);
            VideoPlaybackController.getInstance()
                .getPlayerStateChangeListener()
                .cancel(handleVideoStateChange);
            VideoPlaybackController.getInstance()
                .getProgramBoundaryListener()
                .cancel(handleOnProgramBoundary);
            VideoPlaybackController.getInstance()
                .getPlayableChangeListener()
                .cancel(handlePlayableChange);

            window.removeEventListener('online', () => true);
            window.removeEventListener('offline', () => true);

            clearDebounceTimerVideoPlayerUi();
        };
    }, []);

    // useEffect to refresh the Metadata for standard & fullinfo in case of SOCU.
    useEffect(() => {
        const playable = oIVideoPlayerController.getPlayable();
        if (
            isFullInfoActive &&
            !isFullInfoDataFetched &&
            playable &&
            oIVideoPlayerController.isNetworkConnected()
        ) {
            if (playable.playbackContentType !== PlaybackContentType.LIVE) {
                VideoPlaybackController.getInstance().prepareMetadataByType(
                    playable,
                    MetadataType.FULL_INFO_PREVIEW,
                    oIVideoPlayerController.getPlayerCurrentTime() *
                        Constants.MILLISECOND_IN_ONE_SECOND,
                );
                if (
                    VideoPlaybackController.getInstance().getMetadataState() ===
                    MetadataState.VOD_PREVIEW_READY
                ) {
                    setIsFullInfoDataFetched(true);
                }
            }
        }
    }, [isFullInfoActive, metadataState]);

    useEffect(() => {
        if (!isAppInForeground) {
            onceOnVideoLaunch.current = false;
            dispatch(hideErrorOSD());
        }
        if (isScreenActive && isScreenActive.current) {
            const payload: Payload = new Payload();
            payload.add(
                AnalyticsConstants.Fields.SCREEN_NAME,
                AnalyticsConstants.Screens.LIVE_TV_SCREEN,
            );
            if (isAppInForeground) {
                screenEntryTime.current = Date.now();
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_TRANSITION,
                    AnalyticsConstants.TransitionState.RESUME,
                );
            } else if (!isAppInForeground) {
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_TRANSITION,
                    AnalyticsConstants.TransitionState.SUSPEND,
                );
                payload.add(
                    AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
                    Date.now() - screenEntryTime.current,
                );
            }
            VU.screen(payload);
        }
        return () => {
            isScreenActive.current = true;
            dispatch(hideSpinner());
        };
    }, [isAppInForeground]);

    useEffect(() => {
        if (channel && Object.keys(channel).length && airingOffers?.length) {
            const isNotPlaybackError = !isErrorOSDActive && !isErrorOverlayActive;
            if (!isStillThere) {
                if (!isNotPlaybackError) {
                    dispatch(disableStillThereTimer());
                } else if (playbackState === PlaybackState.PLAY) {
                    dispatch(resetStillThereTimeout(true));
                }
            }
        }
    }, [isErrorOSDActive, isErrorOverlayActive, metadataState, channel]);

    useEffect(() => {
        const state = VideoPlaybackController.getInstance().getMetadataState();
        const isLiveMetadata =
            state === MetadataState.AIRING_READY ||
            state === MetadataState.PREVIEW_READY ||
            state === MetadataState.PREVIEW_ERROR ||
            state === MetadataState.ACTION_LIST_READY;

        const isSocuOrNdvrMetaData =
            state === MetadataState.VOD_PREVIEW_READY ||
            state === MetadataState.VOD_PREVIEW_ERROR ||
            state === MetadataState.VOD_ACTION_READY ||
            state === MetadataState.RECORDING_PREVIEW_READY ||
            state === MetadataState.RECORDING_PREVIEW_ERROR;

        if (isLiveMetadata) {
            if (!onceOnVideoLaunch.current) {
                onceOnVideoLaunch.current = true;
                dispatch(showTrickPlayMenuOnStartUp());
            }
        } else if (isSocuOrNdvrMetaData) {
            dispatch(showTrickPlayMenuOnStartUp());
            onceOnVideoLaunch.current = false;
        } else {
            onceOnVideoLaunch.current = false;
        }
    }, [metadataState]);

    useEffect(() => {
        if (!isTizenPlatform) {
            if (isTrickplayMenuActive) {
                setFullInfoAnimationWait(false);
                setActionsStripAnimationWait(false);
            }
            if (isFullInfoActive) {
                setTrickplayAnimationWait(false);
            }
            if (!isActionsStripActive && isStandardInfoActive && !isFullInfoActive) {
                setActionsStripAnimationWait(false);
            }
        }
        setCurrentTextTrack(oIVideoPlayerController.getSelectedTextTrack());
        setCurrentAudioTrackSelected(oIVideoPlayerController.getSelectedAudioTrack());
        if (
            oIVideoPlayerController.isTrickplayEnabled() &&
            (isFullInfoActive || isActionsStripActive)
        ) {
            dispatch(toggleIsPlaying());
        }

        let timer: NodeJS.Timeout;

        if (!isTizenPlatform) {
            timer = setTimeout(() => {
                setStandardInfoAnimationWait(isStandardInfoActive);
                setFullInfoAnimationWait(isFullInfoActive);
                setActionsStripAnimationWait(isActionsStripActive);
                setTrickplayAnimationWait(isTrickplayMenuActive);
            }, INFO_BANNER_HIDE_ANIMATION_TIMEOUT_VALUE);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [isStandardInfoActive, isFullInfoActive, isActionsStripActive, isTrickplayMenuActive]);

    useEffect(() => {
        if (isSpinnerActive && errorCodes) {
            if (oIVideoPlayerController.isTrickplayEnabled()) {
                dispatch(updateTrickPlayMenuTimer());
                oIVideoPlayerController.resume();
                dispatch(videoPlayerToggleIsPlayingAction(true));
                dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
            }
        }
    }, [isSpinnerActive, errorCodes]);

    useEffect(() => {
        if (isErrorOSDActive || isErrorOverlayActive) {
            if (isNetworkConnected && oIVideoPlayerController.isTrickplayEnabled())
                dispatch(toggleIsPlaying());

            if (isErrorOverlayActive) {
                dispatch(hideInfoBanners());
            }
        }
    }, [isErrorOSDActive, isErrorOverlayActive]);

    useEffect(() => {
        if (isErrorOverlayActive && isOneLineGuideActive) {
            dispatch(hideOneLineGuide());
        }
    }, [isErrorOverlayActive]);

    const infoBannerDiv = (
        <div
            className={`info-banner ${
                isInfobannerContainerActive && !isTizenPlatform ? 'show-info-banner ' : ''
            }
            ${!isInfobannerContainerActive && !isTizenPlatform ? 'hide-info-banner' : ''} `}
            data-testid="info-banner"
        >
            {isStandardInfoActive ||
            standardInfoAnimationWait ||
            (isNetworkConnected && oIVideoPlayerController.isTrickplayEnabled()) ? (
                <StandardInfoBanner
                    mPreviewData={previousOfferPreview.current}
                    mAiringData={previousAiringOffers.current}
                    hideBannerTipAtProgramBoundary={hideBannerTipAtProgramBoundary}
                    setHideBannerTipAtProgramBoundary={setHideBannerTipAtProgramBoundary}
                    channel={previousChannel.current}
                />
            ) : null}
            {isFullInfoActive || fullInfoAnimationWait ? (
                <EventDescription mPreviewData={offerPreview} mAiringData={airingOffers} />
            ) : null}
            {isActionsStripActive || actionsStripAnimationWait ? (
                <ActionsStrip actionItems={actionItems} cursorState={isCursorEnabled} />
            ) : null}
            {isTrickplayMenuActive ||
            trickplayAnimationWait ||
            (isNetworkConnected && oIVideoPlayerController.isTrickplayEnabled()) ? (
                <TrickplayBanner channel={channel} />
            ) : null}
        </div>
    );

    const spinnerPosition: string =
        fullInfoAnimationWait || trickplayAnimationWait || isFullInfoActive || isTrickplayMenuActive
            ? SpinnerPositionTypes.SPINNER_POSITION_TOP
            : SpinnerPositionTypes.DEFAULT;

    if (!isStandardInfoActive && !isFullInfoActive && isActionsStripActive) {
        osdType = OsdTypes.MINIMIZED_FULL_INFO_BANNER;
    } else if (isTrickplayMenuActive) {
        osdType = OsdTypes.TRICKPLAY_MENU;
    } else if (isFullInfoActive && isAdult) {
        osdType = OsdTypes.FULL_INFO_BANNER_ADULT;
    } else if (isFullInfoActive) {
        osdType = OsdTypes.FULL_INFO_BANNER;
    } else if (isStandardInfoActive) {
        osdType = OsdTypes.STANDARD_INFO_BANNER;
    } else if (isOneLineGuideActive) {
        osdType = OsdTypes.ONE_LINE_GUIDE;
    }
    useEffect(() => {
        if (isStillThereOsdActive) {
            // Inactivity OSD
            dispatch(
                showErrorOSD(
                    '',
                    Locale.getString(LocaleKeys.LIVE_TV_INACTIVITY_OSD_MESSAGE),
                    ErrorPriority.STILL_THERE,
                ),
            );
        } else {
            dispatch(hideErrorOSD());
        }
    }, [isStillThereOsdActive]);

    useEffect(() => {
        if (isOverlayDialogActive) {
            dispatch(hideErrorOSD());
        } else if (!isErrorOSDActive && errorPriority) {
            dispatch(showErrorOSD(errorCode, errorMessage, errorPriority));
        }
    }, [isOverlayDialogActive]);

    useEffect(() => {
        if (isErrorOSDActive) {
            if (isOverlayDialogActive) dispatch(hideOverlayDialog());
        }
    }, [isErrorOSDActive]);

    return (
        <React.Fragment>
            <ToastMessage type={TOAST_MESSAGE_TYPE.FROM_TOP_LEFT} />
            {isCursorEnabled && <RootInteractiveLayer />}
            {!NetworkManager.isNetworkDisconnected() && <Whisper message="" />}
            {isErrorOSDActive && <Osd message={errorMessage} code={errorCode} type={osdType} />}
            {isSpinnerActive &&
            !isErrorOSDActive &&
            !oIVideoPlayerController.isTrickplayEnabled() &&
            oIVideoPlayerController.getRestrictionType() !== PlaybackRestrictionType.ADULT ? (
                <Spinner
                    message={spinnerMessage}
                    position={spinnerPosition}
                    channelNumber={channelNumber.current}
                    errorCodes={errorCodes}
                    background={true}
                />
            ) : null}
            {isInfobannerContainerActive || infoBannerDivAnimationWait ? infoBannerDiv : null}
            {isVideoDebugOverlayActive ? <VideoDebugOverlay /> : null}

            {isOneLineGuideActive && <OneLineGuide />}
        </React.Fragment>
    );
};

export default VideoPlayerUI;
